import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  RefreshCw,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Copy,
  ExternalLink,
  ShieldAlert,
  Database,
  Download,
  Key,
  Info,
  Clock,
  ArrowRightLeft,
  Code2,
  Send,
  Eye,
  Save,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import CatalogNavTabs from '@/components/catalog/CatalogNavTabs'
import { buildOutboundSecretPayload } from './inventorySync/outboundSecret'
import { GOOGLE_APPS_SCRIPT_TEMPLATE } from './inventorySync/googleAppsScriptTemplate'
import {
  inventoryApi,
  type InventoryOverview,
  type InventorySyncConfig,
  type SyncResult,
  type OutboundWebhookTestResult,
} from '@/services/inventoryApi'

export default function InventorySyncPage() {
  const [overview, setOverview] = useState<InventoryOverview | null>(null)
  const [config, setConfig] = useState<InventorySyncConfig>({})
  const [sheetUrl, setSheetUrl] = useState('')
  const [sheetGid, setSheetGid] = useState('0')
  const [strictStock, setStrictStock] = useState(false)
  const [lowStockThreshold, setLowStockThreshold] = useState(3)
  const [overwriteEnrichment, setOverwriteEnrichment] = useState(false)

  // Sincronización bidireccional saliente
  const [webhookUrl, setWebhookUrl] = useState('')
  const [outboundWebhookUrl, setOutboundWebhookUrl] = useState('')
  const [outboundWebhookSecret, setOutboundWebhookSecret] = useState('')
  const [outboundSyncEnabled, setOutboundSyncEnabled] = useState(true)
  const [showAppsScript, setShowAppsScript] = useState(false)

  // Pruebas en vivo de webhooks salientes
  const [testingGsWebhook, setTestingGsWebhook] = useState(false)
  const [gsWebhookTestResult, setGsWebhookTestResult] = useState<OutboundWebhookTestResult | null>(null)
  const [testingErpWebhook, setTestingErpWebhook] = useState(false)
  const [erpWebhookTestResult, setErpWebhookTestResult] = useState<OutboundWebhookTestResult | null>(null)

  // Visor / Inspector de Payload y Headers
  const [showPayloadInspector, setShowPayloadInspector] = useState(false)
  const [selectedInspectorTab, setSelectedInspectorTab] = useState<'sale' | 'cancel' | 'ping'>('sale')

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [lastTestResult, setLastTestResult] = useState<SyncResult | null>(null)

  // El secreto guardado nunca vuelve del backend: solo se envía si se escribe uno nuevo.
  const outboundSecretPayload = () => buildOutboundSecretPayload(outboundWebhookSecret)
  const applySavedConfig = (updated: InventorySyncConfig) => {
    setConfig(updated)
    // Ya está cifrado en el servidor; el campo solo sirve para reemplazarlo.
    setOutboundWebhookSecret('')
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [overviewData, configData] = await Promise.all([
        inventoryApi.getOverview().catch(() => null),
        inventoryApi.getConfig().catch(() => ({} as InventorySyncConfig)),
      ])

      if (overviewData) setOverview(overviewData)
      if (configData) {
        setConfig(configData)
        if (configData.googleSheets?.sheetUrl) {
          setSheetUrl(configData.googleSheets.sheetUrl)
        }
        if (configData.googleSheets?.sheetGid) {
          setSheetGid(configData.googleSheets.sheetGid)
        }
        if (configData.googleSheets?.webhookUrl) {
          setWebhookUrl(configData.googleSheets.webhookUrl)
        }
        if (configData.outboundWebhookUrl) {
          setOutboundWebhookUrl(configData.outboundWebhookUrl)
        }
        setOutboundWebhookSecret('')
        if (typeof configData.outboundSyncEnabled === 'boolean') {
          setOutboundSyncEnabled(configData.outboundSyncEnabled)
        }
        setStrictStock(Boolean(configData.strictStock))
        if (typeof configData.lowStockThreshold === 'number') {
          setLowStockThreshold(configData.lowStockThreshold)
        }
      }
    } catch (err) {
      console.error('Error loading inventory sync data:', err)
      toast.error('Error al cargar la información de inventario')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveConfig = async (customMessage?: unknown) => {
    try {
      setSaving(true)
      const updated = await inventoryApi.updateConfig({
        strictStock,
        lowStockThreshold,
        outboundWebhookUrl: outboundWebhookUrl.trim(),
        ...outboundSecretPayload(),
        outboundSyncEnabled,
        googleSheets: {
          sheetUrl: sheetUrl.trim(),
          sheetGid: sheetGid.trim() || '0',
          webhookUrl: webhookUrl.trim(),
        },
      })
      applySavedConfig(updated)
      const msg = typeof customMessage === 'string' ? customMessage : 'Configuración de inventario guardada'
      toast.success(msg)
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al guardar configuración')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleOutboundSync = async (enabled: boolean) => {
    setOutboundSyncEnabled(enabled)
    try {
      const updated = await inventoryApi.updateConfig({
        strictStock,
        lowStockThreshold,
        outboundWebhookUrl: outboundWebhookUrl.trim(),
        ...outboundSecretPayload(),
        outboundSyncEnabled: enabled,
        googleSheets: {
          sheetUrl: sheetUrl.trim(),
          sheetGid: sheetGid.trim() || '0',
          webhookUrl: webhookUrl.trim(),
        },
      })
      applySavedConfig(updated)
      toast.success(
        enabled
          ? 'Sincronización bidireccional activada y guardada'
          : 'Sincronización bidireccional pausada y guardada'
      )
    } catch {
      toast.error('Error al guardar el estado de sincronización')
    }
  }

  const hasUnsavedOutboundChanges =
    (config.googleSheets?.webhookUrl || '') !== webhookUrl.trim() ||
    (config.outboundWebhookUrl || '') !== outboundWebhookUrl.trim() ||
    outboundWebhookSecret.trim() !== '' ||
    Boolean(config.outboundSyncEnabled ?? true) !== outboundSyncEnabled

  const handleTestConnection = async () => {
    if (!sheetUrl.trim()) {
      toast.error('Ingresa la URL de la hoja de Google Sheets')
      return
    }

    try {
      setTesting(true)
      setLastTestResult(null)
      const result = await inventoryApi.testConnection(sheetUrl.trim(), sheetGid.trim() || '0')
      setLastTestResult(result)
      if (result.success) {
        toast.success(`Conexión exitosa: ${result.processed} productos detectados`)
      } else {
        toast.error(`Atención: se encontraron errores al leer la hoja`)
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Error al conectar'
      toast.error(msg)
    } finally {
      setTesting(false)
    }
  }

  const handleSyncNow = async () => {
    if (!sheetUrl.trim()) {
      toast.error('Ingresa la URL de la hoja de Google Sheets')
      return
    }

    try {
      setSyncing(true)
      // Guardar antes de sincronizar
      const updated = await inventoryApi.updateConfig({
        strictStock,
        lowStockThreshold,
        outboundWebhookUrl: outboundWebhookUrl.trim(),
        ...outboundSecretPayload(),
        outboundSyncEnabled,
        googleSheets: {
          sheetUrl: sheetUrl.trim(),
          sheetGid: sheetGid.trim() || '0',
          webhookUrl: webhookUrl.trim(),
        },
      })
      applySavedConfig(updated)

      const result = await inventoryApi.syncNow({
        sheetUrl: sheetUrl.trim(),
        sheetGid: sheetGid.trim() || '0',
        overwriteEnrichment,
      })

      setLastTestResult(result)
      if (result.success) {
        toast.success(`Sincronización completada: ${result.created} creados, ${result.updated} actualizados`)
        void loadData()
      } else {
        toast.error(`Sincronización parcial: ${result.errors.length} errores`)
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Error al sincronizar'
      toast.error(msg)
    } finally {
      setSyncing(false)
    }
  }

  const handleGenerateApiKey = async () => {
    if (!confirm('¿Deseas generar una nueva API Key de integración? La anterior dejará de funcionar.')) {
      return
    }
    try {
      const updated = await inventoryApi.updateConfig({ generateNewApiKey: true })
      setConfig(updated)
      toast.success('Nueva API Key de inventario generada')
    } catch {
      toast.error('Error al generar API Key')
    }
  }

  const handleGenerateSecret = () => {
    const bytes = new Uint8Array(24)
    window.crypto.getRandomValues(bytes)
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
    setOutboundWebhookSecret(hex)
    toast.success('Secreto HMAC seguro generado (48 caracteres hex)')
  }

  const handleClearOutboundSecret = async () => {
    if (!config.outboundWebhookSecretSet) return
    if (
      !confirm(
        '¿Quitar el secreto HMAC? Los envíos al ERP dejarán de ir firmados hasta que configures uno nuevo.'
      )
    ) {
      return
    }
    try {
      setSaving(true)
      const updated = await inventoryApi.updateConfig({ clearOutboundWebhookSecret: true })
      applySavedConfig(updated)
      toast.success('Secreto HMAC eliminado')
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al quitar el secreto')
    } finally {
      setSaving(false)
    }
  }

  const handleTestGsWebhook = async () => {
    if (!webhookUrl.trim()) {
      toast.error('Ingresa la URL de la Web App de Google Apps Script')
      return
    }
    try {
      setTestingGsWebhook(true)
      setGsWebhookTestResult(null)
      const res = await inventoryApi.testWebhook({
        target: 'google_sheets',
        webhookUrl: webhookUrl.trim(),
      })
      setGsWebhookTestResult(res)
      if (res.success) {
        toast.success(`Ping exitoso a Google Sheets (${res.durationMs}ms, HTTP ${res.statusCode || 200})`)
        try {
          const updated = await inventoryApi.updateConfig({
            strictStock,
            lowStockThreshold,
            outboundWebhookUrl: outboundWebhookUrl.trim(),
            ...outboundSecretPayload(),
            outboundSyncEnabled,
            googleSheets: {
              sheetUrl: sheetUrl.trim(),
              sheetGid: sheetGid.trim() || '0',
              webhookUrl: webhookUrl.trim(),
            },
          })
          applySavedConfig(updated)
          toast.success('URL de Google Sheets guardada automáticamente')
        } catch {
          // ignore auto-save error if already reported
        }
      } else {
        toast.error(`Error en Google Sheets: ${res.error || `HTTP ${res.statusCode}`}`)
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'Error al probar webhook')
    } finally {
      setTestingGsWebhook(false)
    }
  }

  const handleTestErpWebhook = async () => {
    if (!outboundWebhookUrl.trim()) {
      toast.error('Ingresa la URL del webhook de tu ERP')
      return
    }
    try {
      setTestingErpWebhook(true)
      setErpWebhookTestResult(null)
      const res = await inventoryApi.testWebhook({
        target: 'erp',
        webhookUrl: outboundWebhookUrl.trim(),
        secret: outboundWebhookSecret.trim() || undefined,
      })
      setErpWebhookTestResult(res)
      if (res.success) {
        toast.success(`Ping exitoso a ERP (${res.durationMs}ms, HTTP ${res.statusCode || 200})`)
        try {
          const updated = await inventoryApi.updateConfig({
            strictStock,
            lowStockThreshold,
            outboundWebhookUrl: outboundWebhookUrl.trim(),
            ...outboundSecretPayload(),
            outboundSyncEnabled,
            googleSheets: {
              sheetUrl: sheetUrl.trim(),
              sheetGid: sheetGid.trim() || '0',
              webhookUrl: webhookUrl.trim(),
            },
          })
          applySavedConfig(updated)
          toast.success('Configuración de ERP guardada automáticamente')
        } catch {
          // ignore
        }
      } else {
        toast.error(`Error en ERP: ${res.error || `HTTP ${res.statusCode}`}`)
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'Error al probar webhook')
    } finally {
      setTestingErpWebhook(false)
    }
  }

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} copiada al portapapeles`)
    } catch {
      toast.error('No se pudo copiar')
    }
  }

  const downloadTemplate = async () => {
    try {
      const blob = await inventoryApi.downloadSheetsTemplate()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'catalogo_inventario.csv'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('No se pudo descargar la plantilla de inventario')
    }
  }

  return (
    <div className="space-y-6 pb-12">
      <CatalogNavTabs />

      {loading && (
        <div className="flex items-center justify-center p-4 text-slate-400 gap-2">
          <RefreshCw className="h-4 w-4 animate-spin text-[#075f55]" />
          <span className="text-xs">Cargando estado de inventario...</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <RefreshCw className="h-6 w-6 text-[#075f55]" />
            Sincronización de Inventario
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Conecta tu hoja de Google Sheets o sistema administrativo como fuente de verdad en tiempo real.
          </p>
        </div>

        <button
          onClick={downloadTemplate}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition shadow-sm w-fit"
        >
          <Download className="h-4 w-4 text-slate-500" />
          Descargar Plantilla CSV
        </button>
      </div>

      {/* Metrics / Status Grid */}
      <div data-tour="inventory-overview" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-700 rounded-xl">
            <Database className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500">Total en Catálogo</div>
            <div className="text-xl font-bold text-slate-900">{overview?.totalProducts ?? 0}</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500">Con Stock Disponible</div>
            <div className="text-xl font-bold text-emerald-700">{overview?.inStockProducts ?? 0}</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-amber-50 text-amber-700 rounded-xl">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500">Stock Crítico (≤ {lowStockThreshold})</div>
            <div className="text-xl font-bold text-amber-600">{overview?.lowStockProducts ?? 0}</div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-purple-50 text-purple-700 rounded-xl">
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500">Última Sincronización</div>
            <div className="text-xs font-bold text-slate-800">
              {overview?.lastSync?.syncedAt
                ? new Date(overview.lastSync.syncedAt).toLocaleString([], {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })
                : 'Sin sincronizaciones'}
            </div>
          </div>
        </div>
      </div>

      {/* Main Configuration Card: Google Sheets */}
      <div data-tour="inventory-sheets" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 text-emerald-800 rounded-xl">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Integración con Google Sheets</h2>
              <p className="text-xs text-slate-500">
                Mantén precios y existencias al día editando directamente tu hoja de cálculo.
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Recomendado para Tiendas
          </span>
        </div>

        <div className="p-5 space-y-5">
          {/* Instructions Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 space-y-2">
            <div className="font-semibold text-slate-800 flex items-center gap-1.5">
              <Info className="h-4 w-4 text-[#075f55]" />
              ¿Cómo conectar tu Google Sheet?
            </div>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Crea o abre tu hoja con columnas como: <strong>SKU, Producto, Precio USD, Stock, Categoría</strong>.</li>
              <li>Haz clic en el botón <strong>Compartir</strong> en Google Sheets y selecciona <strong>"Cualquier persona con el enlace puede leer"</strong> (o Archivo &gt; Compartir &gt; Publicar en la web).</li>
              <li>Pega el enlace de tu navegador aquí abajo y presiona <strong>Probar Conexión</strong>.</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3 space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">URL de la Hoja de Google Sheets</label>
              <input
                type="url"
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/1BxiMVs0XRA5.../edit"
                className="w-full text-sm px-3.5 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#075f55]/20 focus:border-[#075f55] outline-none transition"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">ID de Pestaña (GID)</label>
              <input
                type="text"
                value={sheetGid}
                onChange={(e) => setSheetGid(e.target.value)}
                placeholder="0"
                className="w-full text-sm px-3.5 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#075f55]/20 focus:border-[#075f55] outline-none transition"
              />
            </div>
          </div>

          {/* Options */}
          <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
              <input
                type="checkbox"
                checked={!overwriteEnrichment}
                onChange={(e) => setOverwriteEnrichment(!e.target.checked)}
                className="rounded text-[#075f55] focus:ring-[#075f55] h-4 w-4"
              />
              <span className="font-medium">
                Preservar descripciones y palabras clave mejoradas por IA
              </span>
              <span className="text-slate-400 font-normal">
                (El sync actualizará precio y stock sin borrar fotos ni textos optimizados)
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              onClick={handleTestConnection}
              disabled={testing || syncing || !sheetUrl.trim()}
              className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-xl transition flex items-center gap-2 disabled:opacity-50"
            >
              {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
              Probar Conexión
            </button>

            <button
              onClick={handleSyncNow}
              disabled={syncing || testing || !sheetUrl.trim()}
              className="px-4 py-2 text-xs font-semibold text-white bg-[#075f55] hover:bg-[#064e46] rounded-xl transition shadow-sm flex items-center gap-2 disabled:opacity-50"
            >
              {syncing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Sincronizar Ahora
            </button>

            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-transparent rounded-xl transition"
            >
              {saving ? 'Guardando...' : 'Guardar Enlace'}
            </button>
          </div>

          {/* Preview / Results box */}
          {lastTestResult && (
            <div
              className={`p-4 rounded-xl border text-xs space-y-3 ${
                lastTestResult.success
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-amber-50 border-amber-200 text-amber-900'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="font-bold flex items-center gap-1.5 text-sm">
                  {lastTestResult.success ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                  )}
                  <span>{lastTestResult.summary}</span>
                </div>
                <Link
                  to="/app/products"
                  className="inline-flex items-center gap-1 font-semibold text-emerald-800 hover:text-emerald-950 underline text-xs"
                >
                  Ver en Catálogo de Productos →
                </Link>
              </div>

              {/* Items Breakdown Table */}
              {lastTestResult.items && lastTestResult.items.length > 0 && (
                <div className="mt-3 bg-white border border-emerald-200/80 rounded-xl overflow-hidden shadow-sm">
                  <div className="bg-emerald-100/60 px-3.5 py-2 border-b border-emerald-200 flex items-center justify-between">
                    <span className="font-bold text-emerald-900 text-xs flex items-center gap-1.5">
                      <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-700" />
                      Detalle de Productos Procesados ({lastTestResult.items.length})
                    </span>
                    <span className="text-[11px] text-emerald-700 font-medium">
                      Valores sincronizados a la base de datos
                    </span>
                  </div>

                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-2">SKU</th>
                          <th className="px-3 py-2">Producto</th>
                          <th className="px-3 py-2 text-center">Stock</th>
                          <th className="px-3 py-2 text-right">Precio USD</th>
                          <th className="px-3 py-2 text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-normal">
                        {lastTestResult.items.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/80 transition">
                            <td className="px-3 py-2 font-mono font-semibold text-slate-800">
                              {item.sku}
                            </td>
                            <td className="px-3 py-2 text-slate-700 truncate max-w-[200px]" title={item.name}>
                              {item.name || '—'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                {item.stock ?? 0} unid.
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700 font-medium">
                              {typeof item.price === 'number' ? `$${item.price.toFixed(2)}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {item.action === 'created' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800">
                                  Nuevo Creado
                                </span>
                              ) : item.action === 'updated' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                                  Actualizado
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600">
                                  Omitido
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-slate-50 px-3.5 py-2 border-t border-slate-200 text-[11px] text-slate-600 flex items-center gap-1.5">
                    <Info className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                    <span>
                      Solo los productos presentes en la hoja de cálculo son actualizados. Los demás productos del catálogo mantienen sus valores vigentes.
                    </span>
                  </div>
                </div>
              )}

              {lastTestResult.errors.length > 0 && (
                <div className="mt-2 text-red-700 space-y-1">
                  <div className="font-semibold">Errores reportados:</div>
                  <ul className="list-disc pl-5">
                    {lastTestResult.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>
                        SKU {err.sku}: {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* JIT Verification Settings */}
      <div data-tour="inventory-jit" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-100 text-blue-800 rounded-xl">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Verificación JIT (Just-in-Time) y Políticas de Stock
            </h2>
            <p className="text-xs text-slate-500">
              Garantiza que el bot de WhatsApp nunca venda productos que se hayan agotado en la tienda física.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-slate-800">Modo Estricto de Stock (strictStock)</div>
                <div className="text-[11px] text-slate-500">
                  Bloquea el cierre si no se puede verificar stock en vivo
                </div>
              </div>
              <input
                type="checkbox"
                checked={strictStock}
                onChange={(e) => setStrictStock(e.target.checked)}
                className="h-5 w-5 rounded text-[#075f55] focus:ring-[#075f55]"
              />
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Si está activo y la hoja o ERP no responde, el bot pedirá validar con un asesor antes de cerrar. Ideal para tiendas de repuestos o tecnología con piezas únicas.
            </p>
          </div>

          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <div>
              <div className="text-xs font-bold text-slate-800">Umbral de Stock de Riesgo</div>
              <div className="text-[11px] text-slate-500">
                Cantidad a partir de la cual el bot verifica en caliente
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={0}
                max={20}
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(Number(e.target.value))}
                className="w-24 text-sm px-3 py-1.5 border border-slate-300 rounded-xl"
              />
              <span className="text-xs text-slate-500">unidades o menos</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bidirectional Outbound Sales Synchronization Card */}
      <div data-tour="inventory-outbound" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 text-emerald-800 rounded-xl">
              <ArrowRightLeft className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Sincronización Bidireccional: Descuento y Reposición Automática
              </h2>
              <p className="text-xs text-slate-500">
                Al concretar una venta o cancelar un pedido en WhatsApp, notifica en vivo a Google Sheets o a tu ERP para sincronizar existencias.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-700">Notificaciones Salientes</label>
            <input
              type="checkbox"
              checked={outboundSyncEnabled}
              onChange={(e) => handleToggleOutboundSync(e.target.checked)}
              className="h-4 w-4 rounded text-[#075f55] focus:ring-[#075f55]"
            />
          </div>
        </div>

        {outboundSyncEnabled && (
          <div className="space-y-4 pt-2">
            {/* Google Sheets Apps Script Webhook */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
                  <span className="text-xs font-bold text-slate-800">Descuento y Devolución en Google Sheets</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleTestGsWebhook}
                    disabled={testingGsWebhook || !webhookUrl.trim()}
                    className="px-2.5 py-1 text-xs font-semibold text-emerald-800 bg-emerald-100 hover:bg-emerald-200 border border-emerald-300 rounded-lg transition flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {testingGsWebhook ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Probar Webhook Saliente
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAppsScript(!showAppsScript)}
                    className="text-xs font-semibold text-[#075f55] hover:underline flex items-center gap-1"
                  >
                    <Code2 className="h-3.5 w-3.5" />
                    {showAppsScript ? 'Ocultar Código Apps Script' : 'Ver Código Apps Script'}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700">
                    URL de la Aplicación Web (Google Apps Script Web App URL)
                  </label>
                  {config.googleSheets?.webhookUrl === webhookUrl.trim() && webhookUrl.trim() !== '' ? (
                    <span className="text-[11px] font-medium text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Guardada
                    </span>
                  ) : webhookUrl.trim() !== '' ? (
                    <span className="text-[11px] font-medium text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3.5 w-3.5" /> Sin guardar
                    </span>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://script.google.com/macros/s/AKfycbx.../exec"
                    className="w-full text-xs px-3.5 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#075f55]/20 focus:border-[#075f55] outline-none transition bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveConfig('URL de Google Sheets guardada con éxito')}
                    disabled={saving || !webhookUrl.trim()}
                    className="px-3.5 py-2 text-xs font-semibold text-white bg-[#075f55] hover:bg-[#064e46] rounded-xl transition shadow-sm flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
                    title="Guardar URL de Google Sheets"
                  >
                    {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Guardar
                  </button>
                </div>
                <p className="text-[11px] text-slate-500">
                  Pega aquí la URL resultante de Implementar como Aplicación Web en tu Google Sheet (con acceso "Cualquier usuario").
                </p>
              </div>

              {/* Feedback del Test de Google Sheets Webhook */}
              {gsWebhookTestResult && (
                <div
                  className={`p-3 rounded-xl border text-xs ${
                    gsWebhookTestResult.success
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-red-50 border-red-200 text-red-900'
                  }`}
                >
                  <div className="flex items-center justify-between font-bold">
                    <span className="flex items-center gap-1.5">
                      {gsWebhookTestResult.success ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      {gsWebhookTestResult.success ? 'Conexión con Google Sheets Exitosa' : 'Error en Conexión con Google Sheets'}
                    </span>
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-white/70 border border-slate-200">
                      HTTP {gsWebhookTestResult.statusCode || 200} • {gsWebhookTestResult.durationMs}ms
                    </span>
                  </div>
                  {gsWebhookTestResult.error && (
                    <div className="mt-1 text-red-700 font-semibold">{gsWebhookTestResult.error}</div>
                  )}
                  {gsWebhookTestResult.responseBody && (
                    <div className="mt-1.5 text-[11px] font-mono bg-white/60 p-2 rounded border border-slate-200 break-all">
                      Respuesta: {gsWebhookTestResult.responseBody}
                    </div>
                  )}
                </div>
              )}

              {showAppsScript && (
                <div className="space-y-4 pt-1">
                  {/* Guía Paso a Paso */}
                  <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 space-y-3">
                    <div className="font-bold flex items-center gap-2 text-amber-950 text-sm">
                      <Info className="h-4 w-4 text-amber-700" />
                      Guía Paso a Paso: Cómo instalar el Apps Script en tu Google Sheet
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div className="bg-white/80 p-3 rounded-lg border border-amber-200/60 space-y-1">
                        <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-bold">1</span>
                          Abrir el Editor de Scripts
                        </div>
                        <p className="text-slate-600 pl-6 leading-relaxed">
                          En tu hoja de cálculo ve al menú superior: <strong>Extensiones &gt; Apps Script</strong>.
                        </p>
                      </div>

                      <div className="bg-white/80 p-3 rounded-lg border border-amber-200/60 space-y-1">
                        <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-bold">2</span>
                          Pegar el Código y Guardar
                        </div>
                        <p className="text-slate-600 pl-6 leading-relaxed">
                          Borra cualquier código en <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">Código.gs</code>, pega el código de abajo y haz clic en <strong>Guardar proyecto 💾</strong>.
                        </p>
                      </div>

                      <div className="bg-white/80 p-3 rounded-lg border border-amber-200/60 space-y-1">
                        <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-bold">3</span>
                          Autorizar Permisos (1 sola vez)
                        </div>
                        <p className="text-slate-600 pl-6 leading-relaxed">
                          En la barra superior selecciona la función <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">autorizarPermisos</code> y pulsa <strong>Ejecutar</strong>. Acepta los permisos de Google (<em>Configuración avanzada &gt; Ir al proyecto &gt; Permitir</em>).
                        </p>
                      </div>

                      <div className="bg-white/80 p-3 rounded-lg border border-amber-200/60 space-y-1">
                        <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[11px] font-bold">4</span>
                          Crear Implementación Web
                        </div>
                        <p className="text-slate-600 pl-6 leading-relaxed">
                          Haz clic en el botón azul <strong>Implementar &gt; Nueva implementación</strong>. Selecciona el tipo <strong>Aplicación web</strong> (icono ⚙️).
                        </p>
                      </div>
                    </div>

                    {/* Alerta Crítica de Permiso Cualquier Usuario */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-900 text-xs space-y-1">
                      <div className="font-bold flex items-center gap-1.5 text-red-950">
                        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                        Configuración Crítica de Seguridad en la Implementación:
                      </div>
                      <ul className="list-disc pl-5 space-y-0.5 text-slate-700">
                        <li><strong>Ejecutar como:</strong> <span className="text-slate-900 font-semibold">"Yo" (tu cuenta de Google)</span>.</li>
                        <li><strong>Quién tiene acceso:</strong> <span className="text-red-700 font-bold bg-red-100 px-1.5 py-0.5 rounded">Cualquier usuario (Anyone)</span>.</li>
                      </ul>
                      <p className="text-[11px] text-red-700 pt-0.5">
                        ⚠️ <strong>Atención:</strong> Si dejas "Solo yo", Google exigirá iniciar sesión y bloqueará las peticiones automáticas con error 401/403.
                      </p>
                    </div>

                    {/* Tip de Actualización */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-blue-900 text-[11px] flex items-start gap-2">
                      <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong>¿Ya creaste una implementación anterior y necesitas cambiar los permisos o el código?</strong><br />
                        Ve a <strong>Implementar &gt; Administrar implementaciones</strong> &gt; Haz clic en el <strong>icono de lápiz (Editar)</strong> &gt; Cambia "Quién tiene acceso" a <strong>"Cualquier usuario"</strong> y en Versión selecciona <strong>"Nueva versión"</strong> &gt; Clic en <strong>Implementar</strong>.
                      </div>
                    </div>
                  </div>

                  {/* Bloque de Código Apps Script */}
                  <div className="bg-slate-900 rounded-xl p-4 text-slate-200 text-xs font-mono space-y-2">
                    <div className="flex items-center justify-between text-slate-400 border-b border-slate-800 pb-2">
                      <span>// Código listo para pegar en Extensiones &gt; Apps Script (Código.gs)</span>
                      <button
                        onClick={() => copyToClipboard(GOOGLE_APPS_SCRIPT_TEMPLATE, 'Código Apps Script')}
                        className="px-2.5 py-1 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-xs flex items-center gap-1.5 font-sans font-semibold transition"
                      >
                        <Copy className="h-3.5 w-3.5" /> Copiar Código Completo
                      </button>
                    </div>
                    <pre className="overflow-x-auto text-[11px] leading-relaxed text-emerald-400 max-h-80 overflow-y-auto">
                      {GOOGLE_APPS_SCRIPT_TEMPLATE}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* ERP Webhook Outbound */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-blue-700" />
                  <span className="text-xs font-bold text-slate-800">Webhook Saliente para ERP / Sistema Propio</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleTestErpWebhook}
                    disabled={testingErpWebhook || !outboundWebhookUrl.trim()}
                    className="px-2.5 py-1 text-xs font-semibold text-blue-800 bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded-lg transition flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {testingErpWebhook ? (
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Probar Webhook de ERP
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPayloadInspector(!showPayloadInspector)}
                    className="text-xs font-semibold text-blue-700 hover:underline flex items-center gap-1"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {showPayloadInspector ? 'Ocultar Inspector' : 'Inspeccionar Payloads'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-700">URL del Endpoint de tu ERP</label>
                    {config.outboundWebhookUrl === outboundWebhookUrl.trim() && outboundWebhookUrl.trim() !== '' ? (
                      <span className="text-[11px] font-medium text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Guardada
                      </span>
                    ) : outboundWebhookUrl.trim() !== '' ? (
                      <span className="text-[11px] font-medium text-amber-600 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" /> Sin guardar
                      </span>
                    ) : null}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={outboundWebhookUrl}
                      onChange={(e) => setOutboundWebhookUrl(e.target.value)}
                      placeholder="https://tu-erp.com/api/v1/ventas-webhook"
                      className="w-full text-xs px-3.5 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#075f55]/20 focus:border-[#075f55] outline-none transition bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => handleSaveConfig('URL de ERP guardada con éxito')}
                      disabled={saving || !outboundWebhookUrl.trim()}
                      className="px-3.5 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition shadow-sm flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
                      title="Guardar URL de ERP"
                    >
                      {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      Guardar
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <label htmlFor="outbound-webhook-secret" className="text-xs font-medium text-slate-600">
                      Secreto HMAC (Firma X-Inventory-Signature)
                    </label>
                    <div className="flex items-center gap-2">
                      {config.outboundWebhookSecretSet && !outboundWebhookSecret && (
                        <>
                          <span className="text-[11px] font-medium text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Configurado
                          </span>
                          <button
                            type="button"
                            onClick={handleClearOutboundSecret}
                            disabled={saving}
                            className="text-[11px] text-red-600 hover:text-red-700 font-semibold flex items-center gap-1 disabled:opacity-50"
                          >
                            <Trash2 className="h-3 w-3" /> Quitar
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={handleGenerateSecret}
                        className="text-[11px] text-[#075f55] hover:text-[#064e46] font-semibold flex items-center gap-1"
                      >
                        <Key className="h-3 w-3" /> Generar Secreto Seguro
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="outbound-webhook-secret"
                      type="text"
                      value={outboundWebhookSecret}
                      onChange={(e) => setOutboundWebhookSecret(e.target.value)}
                      placeholder={
                        config.outboundWebhookSecretSet
                          ? 'Secreto guardado (oculto). Escribe uno nuevo para reemplazarlo'
                          : 'mi-secreto-de-firma'
                      }
                      autoComplete="new-password"
                      aria-describedby="outbound-webhook-secret-help"
                      className="w-full text-xs px-3.5 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#075f55]/20 focus:border-[#075f55] outline-none transition bg-white font-mono"
                    />
                    {outboundWebhookSecret && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(outboundWebhookSecret, 'Secreto HMAC')}
                        className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-xl transition"
                        title="Copiar Secreto"
                        aria-label="Copiar secreto HMAC"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p id="outbound-webhook-secret-help" className="text-[11px] text-slate-500">
                    {outboundWebhookSecret
                      ? 'Guárdalo ahora y cópialo en tu sistema: no se volverá a mostrar.'
                      : config.outboundWebhookSecretSet
                        ? 'El secreto está guardado y cifrado. No se vuelve a mostrar; si lo perdiste, genera uno nuevo.'
                        : 'Opcional. Si lo configuras, cada envío incluye la firma HMAC-SHA256 del cuerpo.'}
                  </p>
                </div>
              </div>

              {/* Feedback del Test de ERP Webhook */}
              {erpWebhookTestResult && (
                <div
                  className={`p-3 rounded-xl border text-xs ${
                    erpWebhookTestResult.success
                      ? 'bg-blue-50 border-blue-200 text-blue-900'
                      : 'bg-red-50 border-red-200 text-red-900'
                  }`}
                >
                  <div className="flex items-center justify-between font-bold">
                    <span className="flex items-center gap-1.5">
                      {erpWebhookTestResult.success ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      {erpWebhookTestResult.success ? 'Conexión con ERP Exitosa' : 'Error en Conexión con ERP'}
                    </span>
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-white/70 border border-slate-200">
                      HTTP {erpWebhookTestResult.statusCode || (erpWebhookTestResult.success ? 200 : 'Error')} • {erpWebhookTestResult.durationMs}ms
                    </span>
                  </div>
                  {erpWebhookTestResult.signature && (
                    <div className="mt-1 text-[11px] font-mono text-slate-700">
                      Firma enviada: <span className="text-blue-700">{erpWebhookTestResult.signature}</span>
                    </div>
                  )}
                  {erpWebhookTestResult.error && (
                    <div className="mt-1 text-red-700 font-semibold">{erpWebhookTestResult.error}</div>
                  )}
                  {erpWebhookTestResult.responseBody && (
                    <div className="mt-1.5 text-[11px] font-mono bg-white/60 p-2 rounded border border-slate-200 break-all">
                      Respuesta ERP: {erpWebhookTestResult.responseBody}
                    </div>
                  )}
                </div>
              )}

              {/* Visor / Inspector de Payload y Headers de Webhooks Salientes */}
              {showPayloadInspector && (
                <div className="bg-slate-900 rounded-xl p-4 text-slate-200 text-xs font-mono space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 font-bold">Eventos de Webhook:</span>
                      <button
                        type="button"
                        onClick={() => setSelectedInspectorTab('sale')}
                        className={`px-2.5 py-1 rounded text-xs transition ${
                          selectedInspectorTab === 'sale'
                            ? 'bg-[#075f55] text-white font-bold'
                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        🛒 Venta (sale_completed)
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedInspectorTab('cancel')}
                        className={`px-2.5 py-1 rounded text-xs transition ${
                          selectedInspectorTab === 'cancel'
                            ? 'bg-amber-600 text-white font-bold'
                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        ↩️ Cancelación (order_cancelled)
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedInspectorTab('ping')}
                        className={`px-2.5 py-1 rounded text-xs transition ${
                          selectedInspectorTab === 'ping'
                            ? 'bg-blue-600 text-white font-bold'
                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        ⚡ Ping (test_ping)
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const code =
                          selectedInspectorTab === 'sale'
                            ? JSON.stringify(
                                {
                                  event: 'inventory.sale_completed',
                                  tenantId: config.apiKey ? 'tu_tenant_id' : 'tenant_demo',
                                  orderId: 'ord_91823',
                                  orderNumber: '#1042',
                                  totalCents: 5100,
                                  currency: 'USD',
                                  customer: { name: 'Carlos Mendoza', phone: '+584121234567' },
                                  timestamp: new Date().toISOString(),
                                  items: [
                                    {
                                      sku: 'PROD-001',
                                      name: 'Pastillas de Freno Corolla 2012',
                                      quantity: 2,
                                      unitPriceCents: 2550,
                                    },
                                  ],
                                },
                                null,
                                2
                              )
                            : selectedInspectorTab === 'cancel'
                            ? JSON.stringify(
                                {
                                  event: 'inventory.order_cancelled',
                                  tenantId: config.apiKey ? 'tu_tenant_id' : 'tenant_demo',
                                  orderId: 'ord_91823',
                                  orderNumber: '#1042',
                                  cancellationReason: 'Cliente solicitó cancelación',
                                  timestamp: new Date().toISOString(),
                                  items: [
                                    {
                                      sku: 'PROD-001',
                                      name: 'Pastillas de Freno Corolla 2012',
                                      quantity: 2,
                                      unitPriceCents: 2550,
                                    },
                                  ],
                                },
                                null,
                                2
                              )
                            : JSON.stringify(
                                {
                                  event: 'inventory.test_ping',
                                  tenantId: 'tu_tenant_id',
                                  timestamp: new Date().toISOString(),
                                  test: true,
                                  message: 'Ping de prueba de conexión desde IridiaChat Inventory Sync',
                                },
                                null,
                                2
                              )
                        copyToClipboard(code, 'Payload JSON')
                      }}
                      className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-white rounded text-[11px] flex items-center gap-1"
                    >
                      <Copy className="h-3 w-3" /> Copiar Payload
                    </button>
                  </div>

                  <div className="space-y-1 text-[11px] text-slate-400">
                    <div>
                      <span className="text-slate-500 font-bold">Headers enviados:</span>
                    </div>
                    <div className="pl-2 space-y-0.5 font-mono text-slate-300">
                      <div>POST {outboundWebhookUrl || 'https://tu-erp.com/api/v1/ventas-webhook'}</div>
                      <div>Content-Type: application/json</div>
                      <div>User-Agent: Iridia-InventorySync/1.0</div>
                      <div>X-Tenant-Id: {'<tu_tenant_id>'}</div>
                      <div>
                        X-Event:{' '}
                        {selectedInspectorTab === 'sale'
                          ? 'inventory.sale_completed'
                          : selectedInspectorTab === 'cancel'
                          ? 'inventory.order_cancelled'
                          : 'inventory.test_ping'}
                      </div>
                      <div>
                        X-Inventory-Signature:{' '}
                        {outboundWebhookSecret || config.outboundWebhookSecretSet
                          ? 'sha256=4f8b91c... (HMAC-SHA256 con tu secreto)'
                          : '(Opcional: configura un secreto para activar la firma criptográfica)'}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-[11px] text-slate-500 font-bold">Cuerpo de la petición (Payload JSON):</div>
                    <pre className="overflow-x-auto text-[11px] leading-relaxed text-emerald-400 bg-slate-950 p-3 rounded-lg border border-slate-800">
                      {selectedInspectorTab === 'sale' &&
                        JSON.stringify(
                          {
                            event: 'sale_completed',
                            tenantId: 'tu_tenant_id',
                            orderId: 'ord_91823',
                            orderNumber: '#1042',
                            totalCents: 5100,
                            currency: 'USD',
                            customer: {
                              name: 'Carlos Mendoza',
                              phone: '+584121234567',
                            },
                            timestamp: '2026-10-14T19:00:00.000Z',
                            items: [
                              {
                                sku: 'PROD-001',
                                name: 'Pastillas de Freno Corolla 2012',
                                quantity: 2,
                                unitPriceCents: 2550,
                              },
                            ],
                          },
                          null,
                          2
                        )}
                      {selectedInspectorTab === 'cancel' &&
                        JSON.stringify(
                          {
                            event: 'order_cancelled',
                            tenantId: 'tu_tenant_id',
                            orderId: 'ord_91823',
                            orderNumber: '#1042',
                            cancellationReason: 'Cliente solicitó cancelación de compra',
                            timestamp: '2026-10-14T19:15:00.000Z',
                            items: [
                              {
                                sku: 'PROD-001',
                                name: 'Pastillas de Freno Corolla 2012',
                                quantity: 2,
                                unitPriceCents: 2550,
                              },
                            ],
                          },
                          null,
                          2
                        )}
                      {selectedInspectorTab === 'ping' &&
                        JSON.stringify(
                          {
                            event: 'inventory.test_ping',
                            tenantId: 'tu_tenant_id',
                            timestamp: '2026-10-14T19:20:00.000Z',
                            test: true,
                            message: 'Ping de prueba de conexión desde IridiaChat Inventory Sync',
                          },
                          null,
                          2
                        )}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Barra de Guardado y Estado de Sincronización Bidireccional */}
            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/80 -mx-5 -mb-5 p-5 rounded-b-2xl">
              <div className="flex items-center gap-2 text-xs">
                {hasUnsavedOutboundChanges ? (
                  <span className="inline-flex items-center gap-1.5 text-amber-800 font-semibold bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    Tienes cambios pendientes por guardar en la sincronización saliente
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-emerald-800 font-medium bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    Sincronización bidireccional guardada y activa
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleSaveConfig('Configuración bidireccional guardada con éxito')}
                disabled={saving}
                className="w-full sm:w-auto px-5 py-2.5 text-xs font-bold text-white bg-[#075f55] hover:bg-[#064e46] rounded-xl transition shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" /> Guardando Cambios...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" /> Guardar Configuración Bidireccional
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Developer / Universal API & Webhook Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-slate-100 text-slate-800 rounded-xl">
            <Key className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">API Universal & Webhooks Externos</h2>
            <p className="text-xs text-slate-500">
              Para programadores, sistemas ERP (Saint, Profit, Odoo) o automatizaciones con Google Apps Script.
            </p>
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700">API Key de Integración</label>
              <button
                onClick={handleGenerateApiKey}
                className="text-[11px] text-blue-600 hover:text-blue-700 font-semibold"
              >
                Regenerar Key
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={config.apiKey || 'No generada'}
                className="w-full text-xs font-mono px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-slate-700"
              />
              <button
                onClick={() => copyToClipboard(config.apiKey || '', 'API Key')}
                className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-xl transition"
                title="Copiar"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl p-4 text-slate-200 text-xs font-mono overflow-x-auto space-y-2">
            <div className="text-slate-400">// Ejemplo de Webhook / Ingesta Delta (cURL / Apps Script)</div>
            <div>
              POST /inventory/delta
              <br />
              Headers: x-api-key: {config.apiKey ? `${config.apiKey.slice(0, 12)}...` : 'tu_api_key'}
              <br />
              Body: &#123; "source": "api", "items": [&#123; "sku": "PROD-1", "stock": 10, "price": 25.5 &#125;] &#125;
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
